import type { FC } from "react"
import { Tooltip as TooltipRechart } from "recharts"
import { Flex, Stack, Text } from "@chakra-ui/react"

import type { Account } from "query/graphql"
import { labelNumber } from "common/helpers/number"
import { MIN_VOTES_TO_SHOW_AVATAR } from "common/constants/chart"
import type { TimelineDot } from "proposal/helpers/chart"

type TooltipProps = {
  voters: Pick<Account, "picture" | "address" | "name">[]
}

const Tooltip: FC<TooltipProps> = ({ voters }) => {
  return (
    <TooltipRechart
      content={({ payload, active }) => {
        if (!payload || !active) return null

        return <Content payload={payload} voters={voters} />
      }}
      position={{ y: -25 }}
    />
  )
}

export default Tooltip

type ContentProps = {
  voters: Pick<Account, "picture" | "address" | "name">[]
  payload: { payload?: TimelineDot }[]
}

const Content: FC<ContentProps> = ({ payload, voters }) => {
  const items = payload
    .filter((p) => Boolean(p.payload))
    .map((p) => p.payload) as TimelineDot[]

  const [firstItem] = items
  const { voter: voterAddress, type, amount } = firstItem

  const voter = voters.find(({ address }) => address === voterAddress)
  const weightLabel = labelNumber(amount)

  const hasMinimumVotes = amount >= MIN_VOTES_TO_SHOW_AVATAR

  if (!voter || !hasMinimumVotes) return null

  const { name } = voter

  return (
    <Stack
      bg="white"
      className="custom-line-chart-tooltip"
      justify="center"
      pb={4}
      pt={3}
      px={6}
      w="md"
    >
      <Flex align="center" justify="space-between" mb={2} w="full">
        <Text
          className="label"
          color="gray.700"
          fontSize="sm"
          fontWeight="semibold"
          mr={2}
        >
          User
        </Text>
        <Text color="gray.500" fontSize="sm">
          {name}
        </Text>
      </Flex>
      <Flex align="center" justify="space-between" mb={2} w="full">
        <Text
          className="label"
          color="gray.700"
          fontSize="sm"
          fontWeight="semibold"
          mr={2}
        >
          Address
        </Text>
        <Text color="gray.500" fontSize="sm">
          {voterAddress}
        </Text>
      </Flex>
      <Flex align="center" justify="space-between" mb={2} w="full">
        <Text
          className="label"
          color="gray.700"
          fontSize="sm"
          fontWeight="semibold"
          mr={2}
        >
          {`User voted ${type.toLocaleLowerCase()} with`}
        </Text>
        <Text color="gray.500" fontSize="sm">
          {`${weightLabel} ${amount === 0 ? "vote" : "votes"}`}
        </Text>
      </Flex>
    </Stack>
  )
}
